import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button, Select, Tag, Space, Typography, Empty, Spin, message,
  Popconfirm, Popover, Tooltip, Divider, Modal,
} from 'antd';
import {
  LinkOutlined, DisconnectOutlined, PlusOutlined, EditOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import * as api from 'util/api';

const { Text } = Typography;

const rowWrap = {
  width: '100%',
  minWidth: 0,
  borderBottom: '1px solid #f0f0f0',
  padding: '12px 0',
};
const rowInner = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};
const mainBlock = {
  flex: '1 1 220px',
  minWidth: 0,
};
const metaLine = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 12px',
  alignItems: 'baseline',
  fontSize: 12,
  color: 'rgba(0, 0, 0, 0.45)',
  marginTop: 6,
  lineHeight: 1.5,
};
const nowrapStat = { whiteSpace: 'nowrap' };

const SERVING_TYPE_OPTIONS = [
  { value: 'off_prem_single', label: 'Off-Prem Single' },
  { value: 'on_prem_single', label: 'On-Prem Single' },
  { value: 'off_prem_pack', label: 'Off-Prem Pack' },
  { value: 'off_prem_slab', label: 'Off-Prem Slab' },
  { value: 'keg_parent', label: 'Keg Parent' },
  { value: 'tap_pour_100', label: 'Tap Pour 100ml' },
  { value: 'tap_pour_150', label: 'Tap Pour 150ml' },
  { value: 'tap_pour_285', label: 'Tap Pour 285ml' },
  { value: 'tap_pour_440', label: 'Tap Pour 440ml' },
  { value: 'tap_pour_500', label: 'Tap Pour 500ml' },
  { value: 'tap_takeaway_1L', label: 'Tap Takeaway 1L' },
];

const kountaProductWebUrl = (kountaProductId) => (
  `https://my.kounta.com/product/update?sub=productinfo&id=${kountaProductId}`
);

const SERVING_TYPE_COLORS = {
  off_prem_single: 'green',
  on_prem_single: 'blue',
  off_prem_pack: 'cyan',
  off_prem_slab: 'geekblue',
  keg_parent: 'orange',
  tap_pour_100: 'purple',
  tap_pour_150: 'purple',
  tap_pour_285: 'purple',
  tap_pour_440: 'purple',
  tap_pour_500: 'purple',
  tap_takeaway_1L: 'magenta',
};

const KountaProductLinker = (
  {
    skuKey,
    onLinksChanged,
    onCatalogSkuChange,
    compact,
    sharedKountaCatalog,
    startOpen,
  },
) => {
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [candidatesPopoverOpen, setCandidatesPopoverOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedServingType, setSelectedServingType] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkingAll, setLinkingAll] = useState(false);
  const [editingServingId, setEditingServingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(Boolean(startOpen));

  const loadData = async () => {
    if (!skuKey) return;
    if (sharedKountaCatalog === null) return;
    setLoading(true);
    try {
      const [linked, cand] = await Promise.all([
        api.getKountaProducts({ skuKey }),
        api.getKountaCandidates(skuKey),
      ]);
      setLinkedProducts(linked);
      setCandidates(cand || []);
      if (sharedKountaCatalog !== undefined && sharedKountaCatalog !== null) {
        setAllProducts(sharedKountaCatalog);
      } else {
        const all = await api.getKountaProducts();
        setAllProducts(all);
      }
    } catch (err) {
      console.error('Error loading Kounta products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sharedKountaCatalog !== undefined && sharedKountaCatalog !== null) {
      setAllProducts(sharedKountaCatalog);
    }
  }, [sharedKountaCatalog]);

  const usingSharedCatalog = sharedKountaCatalog !== undefined && sharedKountaCatalog !== null;
  const catalogPending = sharedKountaCatalog === null;
  const sharedCatalogRef = useRef(sharedKountaCatalog);
  sharedCatalogRef.current = sharedKountaCatalog;

  // Reload this SKU's links when the SKU changes, or when a shared catalog first arrives.
  // Later catalog patches only update allProducts (effect above), so sibling cards stay put.
  useEffect(() => {
    if (!skuKey || catalogPending) return;
    loadData();
  }, [skuKey, catalogPending, usingSharedCatalog]);

  const unlinkedOptions = useMemo(() => {
    const linkedIds = new Set(linkedProducts.map((p) => p.id));
    return allProducts
      .filter((p) => !p.productSkuKey && !linkedIds.has(p.id))
      .map((p) => ({
        value: p.id,
        label: `${p.productName} (${p.kountaProductId})`,
        product: p,
      }));
  }, [allProducts, linkedProducts]);

  const refreshAfterLink = async () => {
    try {
      const [linked, cand] = await Promise.all([
        api.getKountaProducts({ skuKey }),
        api.getKountaCandidates(skuKey),
      ]);
      setLinkedProducts(linked);
      setCandidates(cand || []);
      const catalog = sharedCatalogRef.current;
      if (Array.isArray(catalog)) {
        const linkedById = new Map(linked.map((p) => [String(p.id), p]));
        setAllProducts(catalog.map((product) => {
          const match = linkedById.get(String(product.id));
          if (match) {
            return {
              ...product,
              productSkuKey: skuKey,
              servingType: match.servingType,
            };
          }
          if (
            product.productSkuKey != null
            && String(product.productSkuKey) === String(skuKey)
          ) {
            return { ...product, productSkuKey: null };
          }
          return product;
        }));
      } else {
        const all = await api.getKountaProducts();
        setAllProducts(all);
      }
    } catch (err) {
      console.error('Error refreshing Kounta data:', err);
    }
  };

  const handleLink = async (kountaId, servingType, opts = {}) => {
    const { keepModal = false } = opts;
    setLinking(true);
    try {
      const payload = servingType != null && servingType !== ''
        ? servingType
        : undefined;
      await api.linkKountaProduct(kountaId, skuKey, payload);
      onCatalogSkuChange?.(kountaId, skuKey);
      message.success('Kounta product linked');
      if (!keepModal) {
        setShowAdd(false);
      }
      setSelectedProductId(null);
      setSelectedServingType(null);
      await refreshAfterLink();
      onLinksChanged?.();
    } catch (err) {
      message.error('Failed to link product');
    } finally {
      setLinking(false);
    }
  };

  const handleLinkAll = async () => {
    if (!candidates.length) return;
    setLinkingAll(true);
    let successCount = 0;
    for (let i = 0; i < candidates.length; i += 1) {
      const item = candidates[i];
      try {
        const payload = item.suggestedServingType || undefined;
        // eslint-disable-next-line no-await-in-loop
        await api.linkKountaProduct(item.id, skuKey, payload);
        onCatalogSkuChange?.(item.id, skuKey);
        successCount += 1;
      } catch (err) {
        message.error(`Failed to link ${item.productName}`);
      }
    }
    if (successCount > 0) {
      message.success(`Linked ${successCount} product${successCount === 1 ? '' : 's'}`);
    }
    await refreshAfterLink();
    onLinksChanged?.();
    setLinkingAll(false);
  };

  const handleUnlink = async (kountaId) => {
    try {
      await api.unlinkKountaProduct(kountaId);
      onCatalogSkuChange?.(kountaId, null);
      message.success('Kounta product unlinked');
      await refreshAfterLink();
      onLinksChanged?.();
    } catch (err) {
      message.error('Failed to unlink product');
    }
  };

  const handleServingTypeChange = async (kountaId, servingType) => {
    try {
      await api.linkKountaProduct(kountaId, skuKey, servingType);
      onCatalogSkuChange?.(kountaId, skuKey);
      setEditingServingId(null);
      await refreshAfterLink();
      onLinksChanged?.();
    } catch (err) {
      message.error('Failed to update serving type');
    }
  };

  if (loading) {
    return compact ? (
      <div style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
        <Spin size="small" />
      </div>
    ) : (
      <Spin size="small" />
    );
  }

  const servingTypeFullLabel = (st) => {
    if (!st) return 'Serving type pending';
    return SERVING_TYPE_OPTIONS.find((o) => o.value === st)?.label || st;
  };

  const renderLinkedRow = (item, compactRow) => {
    const stFull = item.servingType
      ? servingTypeFullLabel(item.servingType)
      : 'Serving type pending';
    const stDisplay = compactRow && stFull.length > 14
      ? `${stFull.slice(0, 12)}…`
      : stFull;
    const compactTagStyle = compactRow
      ? {
        fontSize: 10,
        padding: '0 3px',
        lineHeight: '16px',
        marginInlineEnd: 0,
        maxWidth: 88,
      }
      : undefined;
    const tag = (
      <Tag
        color={item.servingType ? (SERVING_TYPE_COLORS[item.servingType] || 'default') : 'default'}
        style={compactTagStyle}
      >
        {compactRow ? stDisplay : stFull}
      </Tag>
    );

    if (compactRow) {
      return (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 0,
            minHeight: 22,
            lineHeight: '22px',
          }}
        >
          <Tooltip title={item.productName}>
            <Text
              strong
              ellipsis
              style={{
                flex: '1 1 72px',
                minWidth: 0,
                fontSize: 11,
                lineHeight: '22px',
              }}
            >
              {item.productName}
            </Text>
          </Tooltip>
          <Tooltip title={stFull}>
            {tag}
          </Tooltip>
          <Space size={0} style={{ flexShrink: 0 }}>
            {editingServingId === item.id ? (
              <Select
                size="small"
                placeholder="Type"
                value={item.servingType || undefined}
                options={SERVING_TYPE_OPTIONS}
                onChange={(val) => handleServingTypeChange(item.id, val)}
                onBlur={() => setEditingServingId(null)}
                style={{ width: 112 }}
                allowClear
                autoFocus
              />
            ) : (
              <Tooltip title="Change serving type">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditingServingId(item.id)}
                  style={{ width: 22, height: 22, padding: 0 }}
                />
              </Tooltip>
            )}
            <Popconfirm
              title="Unlink this Kounta product?"
              onConfirm={() => handleUnlink(item.id)}
              okText="Unlink"
              cancelText="Cancel"
            >
              <Tooltip title="Unlink">
                <Button
                  size="small"
                  icon={<DisconnectOutlined />}
                  danger
                  type="text"
                  style={{ width: 22, height: 22, padding: 0 }}
                />
              </Tooltip>
            </Popconfirm>
            <Tooltip title="Open in Kounta">
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                href={kountaProductWebUrl(item.kountaProductId)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: 22, height: 22, padding: 0 }}
              />
            </Tooltip>
          </Space>
        </div>
      );
    }

    return (
      <div key={item.id} style={rowWrap}>
        <div style={rowInner}>
          <div style={mainBlock}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
            }}
            >
              <Text strong style={{ wordBreak: 'break-word' }}>{item.productName}</Text>
              {tag}
            </div>
            <div style={metaLine}>
              <span style={nowrapStat}>
                ID:
                {' '}
                {item.kountaProductId}
                <a
                  href={kountaProductWebUrl(item.kountaProductId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in Kounta"
                  style={{ marginLeft: 6 }}
                >
                  <LinkOutlined />
                </a>
              </span>
              {item.sellPriceIncTax != null && (
                <span style={nowrapStat}>
                  Sell: $
                  {Number(item.sellPriceIncTax).toFixed(2)}
                </span>
              )}
              {item.costPriceIncTax != null && (
                <span style={nowrapStat}>
                  Cost: $
                  {Number(item.costPriceIncTax).toFixed(2)}
                </span>
              )}
              {item.categoryNames && (
                <span style={{ wordBreak: 'break-word', minWidth: 0 }}>
                  {item.categoryNames}
                </span>
              )}
            </div>
          </div>
          <Space
            size="small"
            style={{ flexShrink: 0, marginLeft: 'auto' }}
            wrap
          >
            {editingServingId === item.id ? (
              <Select
                size="small"
                placeholder="Serving type"
                value={item.servingType || undefined}
                options={SERVING_TYPE_OPTIONS}
                onChange={(val) => handleServingTypeChange(item.id, val)}
                onBlur={() => setEditingServingId(null)}
                style={{ minWidth: 168 }}
                allowClear
                autoFocus
              />
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => setEditingServingId(item.id)}
              >
                Change type
              </Button>
            )}
            <Popconfirm
              title="Unlink this Kounta product?"
              onConfirm={() => handleUnlink(item.id)}
              okText="Unlink"
              cancelText="Cancel"
            >
              <Button size="small" icon={<DisconnectOutlined />} danger type="text" />
            </Popconfirm>
            <Tooltip title="Open in Kounta">
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                href={kountaProductWebUrl(item.kountaProductId)}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Tooltip>
          </Space>
        </div>
      </div>
    );
  };

  const candidateRow = (item, inModal) => {
    const st = item.suggestedServingType;
    const stLabel = st ? servingTypeFullLabel(st) : 'Auto';
    const linkedElsewhere = item.productSkuKey != null && item.productSkuKey !== skuKey;

    return (
      <div
        key={item.id}
        style={{
          ...rowWrap,
          padding: (compact && !inModal) ? '6px 0' : '10px 0',
        }}
      >
        <div style={rowInner}>
          <div style={mainBlock}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
            }}
            >
              <Text style={{
                fontSize: (compact && !inModal) ? 12 : 13,
                wordBreak: 'break-word',
              }}
              >
                {item.productName}
              </Text>
              {st && (
                <Tag
                  color={SERVING_TYPE_COLORS[st] || 'default'}
                  style={{ fontSize: (compact && !inModal) ? 10 : 12 }}
                >
                  {stLabel}
                </Tag>
              )}
            </div>
            {linkedElsewhere && (
              <Text type="warning" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                Linked to another SKU — linking will move it here.
              </Text>
            )}
            {item.categoryNames && (!(compact && !inModal)) && (
              <div style={{ ...metaLine, marginTop: 4 }}>
                <span style={{ wordBreak: 'break-word' }}>{item.categoryNames}</span>
              </div>
            )}
          </div>
          <Button
            size="small"
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => handleLink(
              item.id,
              item.suggestedServingType,
              { keepModal: inModal },
            )}
            loading={linking}
            style={{ flexShrink: 0 }}
          >
            Link
          </Button>
        </div>
      </div>
    );
  };

  const candidatesScrollStyle = {
    maxWidth: compact ? 360 : 420,
    maxHeight: 280,
    overflowY: 'auto',
  };

  // ── Compact mode (unchanged — Popover-based) ──────────────────

  const compactCandidatesBlock = (
    <div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
        Candidate matches:
      </Text>
      <div style={candidatesScrollStyle}>
        {candidates.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No rule-based candidates for this SKU.
          </Text>
        ) : (
          candidates.map((item) => candidateRow(item, false))
        )}
      </div>
    </div>
  );

  const compactAddForm = (
    <Space direction="vertical" style={{ marginTop: 0 }} size="small">
      {compactCandidatesBlock}
      <Divider plain style={{ margin: '8px 0' }}>Or search manually</Divider>
      <Space wrap>
        <Select
          showSearch
          placeholder="Search Kounta products..."
          options={unlinkedOptions}
          value={selectedProductId}
          onChange={setSelectedProductId}
          filterOption={(input, option) => (
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          )}
          style={{ width: 280 }}
        />
        <Button
          type="primary"
          size="small"
          disabled={!selectedProductId}
          loading={linking}
          onClick={() => handleLink(selectedProductId, selectedServingType || undefined)}
        >
          Link
        </Button>
      </Space>
    </Space>
  );

  const compactAddPopover = (
    <Popover
      title="Link Kounta products"
      content={compactAddForm}
      trigger="click"
      visible={showAdd}
      onVisibleChange={(v) => {
        setShowAdd(v);
        if (!v) {
          setSelectedProductId(null);
          setSelectedServingType(null);
        }
      }}
    >
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        style={{
          height: 22, padding: '0 6px', fontSize: 11, lineHeight: '20px',
        }}
      >
        {linkedProducts.length ? 'Add' : 'Link'}
      </Button>
    </Popover>
  );

  const truncateName = (name, max = 22) => {
    const s = String(name || '');
    if (s.length <= max) return s;
    return `${s.slice(0, max - 1)}…`;
  };

  const compactLinkedNamesTooltip = linkedProducts
    .map((p) => p.productName)
    .filter(Boolean)
    .join(', ');

  const compactLinksPopoverContent = (
    <div style={{ maxWidth: 400, maxHeight: 380, overflowY: 'auto' }}>
      {linkedProducts.map((item) => renderLinkedRow(item, false))}
      <Divider />
      {compactAddForm}
    </div>
  );

  const compactRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'nowrap',
    minHeight: 22,
    lineHeight: '22px',
    maxWidth: '100%',
    overflow: 'hidden',
  };

  if (compact) {
    return (
      <div style={{ width: '100%', minWidth: 0 }}>
        {linkedProducts.length === 0 && (
          <div style={compactRowStyle}>
            {!showAdd && <Text type="secondary" style={{ fontSize: 11 }}>—</Text>}
            {!showAdd && candidates.length > 0 && (
              <Popover
                title="Candidate matches"
                content={(
                  <div style={candidatesScrollStyle}>
                    {candidates.map((item) => candidateRow(item, false))}
                  </div>
                )}
                trigger="click"
                visible={candidatesPopoverOpen}
                onVisibleChange={setCandidatesPopoverOpen}
              >
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 22, fontSize: 11 }}
                >
                  {candidates.length}
                  {' '}
                  candidate
                  {candidates.length === 1 ? '' : 's'}
                </Button>
              </Popover>
            )}
            {compactAddPopover}
          </div>
        )}

        {linkedProducts.length > 0 && (
          <div style={compactRowStyle}>
            <Popover
              title="Kounta links"
              content={compactLinksPopoverContent}
              trigger="click"
              destroyTooltipOnHide
            >
              <Tooltip title={compactLinkedNamesTooltip}>
                <span style={{
                  cursor: 'pointer', display: 'inline-block', maxWidth: '100%',
                }}
                >
                  <Tag
                    style={{
                      margin: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 11,
                      lineHeight: '20px',
                    }}
                  >
                    {linkedProducts.length === 1
                      ? truncateName(linkedProducts[0].productName, 28)
                      : `${linkedProducts.length} linked`}
                  </Tag>
                </span>
              </Tooltip>
            </Popover>
          </div>
        )}
      </div>
    );
  }

  // ── Non-compact mode (Modal-based) ────────────────────────────

  const summaryTags = linkedProducts.length > 0
    ? linkedProducts.map((p) => (
      <Tag
        key={p.id}
        color={p.servingType ? (SERVING_TYPE_COLORS[p.servingType] || 'default') : 'default'}
        style={{ marginBottom: 4 }}
      >
        {truncateName(p.productName, 30)}
      </Tag>
    ))
    : null;

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      {/* Summary of linked products (outside modal) */}
      {linkedProducts.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {summaryTags}
          </div>
        </div>
      )}

      {linkedProducts.length === 0 && candidates.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No Kounta products linked"
          style={{ margin: '8px 0' }}
        />
      )}

      {linkedProducts.length === 0 && candidates.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {candidates.length}
          {' '}
          candidate match
          {candidates.length === 1 ? '' : 'es'}
          {' '}
          found
        </Text>
      )}

      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
        style={{ marginTop: 8 }}
      >
        {linkedProducts.length > 0 ? 'Manage Kounta Links' : 'Link Kounta Products'}
      </Button>

      <Modal
        title="Kounta Product Links"
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedProductId(null);
          setSelectedServingType(null);
        }}
        footer={(
          <Button type="primary" onClick={() => setModalOpen(false)}>
            Done
          </Button>
        )}
        width={640}
        destroyOnClose={false}
      >
        {/* Candidates section */}
        {candidates.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
            >
              <Text strong style={{ fontSize: 13 }}>
                Candidates (
                {candidates.length}
                )
              </Text>
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={linkingAll}
                onClick={handleLinkAll}
              >
                Add all
              </Button>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {candidates.map((item) => candidateRow(item, true))}
            </div>
          </div>
        )}

        {candidates.length === 0 && linkedProducts.length === 0 && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
            No rule-based candidates for this SKU. Use the search below to find products manually.
          </Text>
        )}

        {/* Linked products section */}
        {linkedProducts.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {candidates.length > 0 && <Divider style={{ margin: '8px 0 12px' }} />}
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              Linked (
              {linkedProducts.length}
              )
            </Text>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {linkedProducts.map((item) => renderLinkedRow(item, false))}
            </div>
          </div>
        )}

        {/* Manual search section */}
        <Divider plain style={{ margin: '8px 0 12px' }}>Search manually</Divider>
        <Space wrap>
          <Select
            showSearch
            placeholder="Search Kounta products..."
            options={unlinkedOptions}
            value={selectedProductId}
            onChange={setSelectedProductId}
            filterOption={(input, option) => (
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            )}
            style={{ width: 340 }}
          />
          <Button
            type="primary"
            size="small"
            disabled={!selectedProductId}
            loading={linking}
            onClick={() => handleLink(
              selectedProductId,
              selectedServingType || undefined,
              { keepModal: true },
            )}
          >
            Link
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Serving type is inferred automatically. Optional override:
          </Text>
          <Select
            placeholder="Override serving type (optional)"
            options={SERVING_TYPE_OPTIONS}
            value={selectedServingType}
            onChange={setSelectedServingType}
            style={{ width: 280, marginTop: 4, display: 'block' }}
            allowClear
            size="small"
          />
        </div>
      </Modal>
    </div>
  );
};

KountaProductLinker.propTypes = {
  skuKey: PropTypes.number,
  onLinksChanged: PropTypes.func,
  onCatalogSkuChange: PropTypes.func,
  compact: PropTypes.bool,
  sharedKountaCatalog: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf([null]),
  ]),
  startOpen: PropTypes.bool,
};

KountaProductLinker.defaultProps = {
  skuKey: null,
  onLinksChanged: undefined,
  onCatalogSkuChange: undefined,
  compact: false,
  sharedKountaCatalog: undefined,
  startOpen: false,
};

export default KountaProductLinker;
